import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PrimaryBlueButton, Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import useAIConfigs from "@/hooks/useAIConfigs";
import LoadingSpinner from "@/components/LoadingSpinner";
import { ScraperJob, ScrapedPage, CreateScraperJobRequest, AzureIndex, JsonImportResult } from "./types";
import CreateScraperJobModal from "./CreateScraperJobModal";
import ScraperJobRow from "./ScraperJobRow";
import ScraperJobResultsModal from "./ScraperJobResultsModal";
import SharePointExportModal from "./SharePointExportModal";
import SharePointFileBrowser from "./SharePointFileBrowser";
import ScraperUsageBanner from "./ScraperUsageBanner";
import PublishToIndexModal from "./PublishToIndexModal";
import useSite from "@/hooks/useSite";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import useAzureIndexesApi from "@/hooks/useAzureIndexesApi";
import useApi from "@/hooks/useApi";
import FileUpload from "@/components/FileUpload/FileUpload";
import FilesEditor from "@/components/FileUpload/FilesEditor";
import YouTubeChannelSearch from "./YouTubeChannelSearch";

const SCRAPER_API_URL = import.meta.env.VITE_SCRAPER_API_URL || "http://localhost:8000/api/v1";
const SCRAPER_API_KEY = import.meta.env.VITE_SCRAPER_API_KEY || "";

const rowPillBase = "inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors border whitespace-nowrap";
const rowPill = {
  blue:  `${rowPillBase} bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100`,
  green: `${rowPillBase} bg-green-600 border-green-600 text-white hover:bg-green-700`,
  red:   `${rowPillBase} bg-red-50 border-red-200 text-red-600 hover:bg-red-100`,
};

type TabType = "sources" | "web" | "file" | "video" | "sharepoint" | "conversation" | "json";

interface IntegrationSource {
  sourceType: string;
  documentCount: number;
  totalChunks: number;
  lastIndexedAt: string | null;
  hasErrors: boolean;
}

const SOURCE_META: Record<string, { label: string; description: string; icon: string; syncKey?: string; tabKey?: TabType }> = {
  bigcommerce_product: { label: "BigCommerce Products", description: "Product catalog — name, SKU, price, availability. Auto-synced daily at 02:30 UTC.", icon: "🛒", syncKey: "bigcommerce" },
  shopify_product:     { label: "Shopify Products",     description: "Product catalog — title, description, variants, price. Auto-synced daily at 02:00 UTC.", icon: "🏪", syncKey: "shopify" },
  website_page:        { label: "Scraped Web Pages",    description: "Pages crawled by the web scraper. Run a new scrape job to update.", icon: "🌐", tabKey: "web" },
  video_transcript:    { label: "Video Transcripts",    description: "Captions and transcripts from YouTube, Vimeo, Dailymotion, TikTok, and 1000+ platforms via yt-dlp.", icon: "🎬", tabKey: "video" },
  kb_article:          { label: "Knowledge Base",       description: "Published KB articles. Synced on publish or manual sync.", icon: "📚", syncKey: "kb" },
  conv_training:       { label: "Conversation Training", description: "Q&A pairs extracted from past conversations with CSAT scores.", icon: "💬" },
  onedrive_file:       { label: "OneDrive / SharePoint", description: "Files synced from Microsoft 365 — PDFs, Word docs, Excel sheets, CSVs. Incremental sync via Graph delta API.", icon: "☁️" },
  custom_content:      { label: "Custom Content",       description: "Manually injected text or markdown via the API.", icon: "📄" },
  price_list:          { label: "Pricing Catalog",      description: "Pricing sheets ingested via API.", icon: "💲" },
};

interface ConvTrainingStatus {
  enabled: boolean;
  vectorReady: boolean;
  totalIndexed: number;
  thisMonth?: number;
  monthlyLimit?: number;
  unlimited?: boolean;
  byChannel: { channel: string; count: number }[];
  recentlyIndexed: {
    conversationId: number;
    channel: string;
    csatScore: number | null;
    chunksIndexed: number;
    indexedAt: string;
  }[];
}

interface ConvInsights {
  total: number;
  indexed: number;
  skipped: number;
  indexedRate: number;
  totalChunks: number;
  byEvent: { event: string; count: number }[];
  recentSkips: {
    conversationId: number;
    event: string;
    detail: string;
    csatScore: number | null;
    messageCount: number | null;
    channel: string | null;
    occurredAt: string;
  }[];
}

interface IndexedConversation {
  conversationId: number;
  channel: string;
  csatScore: number | null;
  chunksIndexed: number;
  indexedAt: string;
}

export default function AINewTraining() {
  const [activeTab, setActiveTab] = useState<TabType>("sources");
  const [integrationSources, setIntegrationSources] = useState<IntegrationSource[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [syncingSource, setSyncingSource] = useState<string | null>(null);
  const [allJobs, setAllJobs] = useState<ScraperJob[]>([]); // Cache all jobs
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, "pending" | "uploading" | "done" | "error">>({});
  const [extractPdfImages, setExtractPdfImages] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ScraperJob | null>(null);
  const [jobResults, setJobResults] = useState<ScrapedPage[] | null>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishingJob, setPublishingJob] = useState<ScraperJob | null>(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoName, setVideoName] = useState("");
  const [expandedActivityJobId, setExpandedActivityJobId] = useState<string | null>(null);
  const [expandedGapReportJobId, setExpandedGapReportJobId] = useState<string | null>(null);
  const [videoSubmitting, setVideoSubmitting] = useState(false);
  const [m365Connected, setM365Connected] = useState(false);
  const [showSharePointExportModal, setShowSharePointExportModal] = useState(false);
  const [spExportJob, setSpExportJob] = useState<ScraperJob | null>(null);
  const [spExportResults, setSpExportResults] = useState<ScrapedPage[]>([]);
  const [convTrainingStatus, setConvTrainingStatus] = useState<ConvTrainingStatus | null>(null);
  const [convInsights, setConvInsights] = useState<ConvInsights | null>(null);
  const [convTrainingLoading, setConvTrainingLoading] = useState(false);
  const [convTrainingExpanded, setConvTrainingExpanded] = useState(true);
  const [convTrainingPage, setConvTrainingPage] = useState(1);
  const [convTrainingConvs, setConvTrainingConvs] = useState<IndexedConversation[]>([]);
  const [convTrainingTotal, setConvTrainingTotal] = useState(0);
  const [convTrainingRemoving, setConvTrainingRemoving] = useState<number | null>(null);
  const [jsonImportFile, setJsonImportFile] = useState<File | null>(null);
  const [jsonImporting, setJsonImporting] = useState(false);
  const [jsonImportResult, setJsonImportResult] = useState<JsonImportResult | null>(null);
  const [quota, setQuota] = useState<{
    maxJobsPerDay: number; maxPagesPerJob: number; maxPagesPerMonth: number;
    jobsToday: number; pagesThisMonth: number;
  } | null>(null);
  const [publishedJobIds, setPublishedJobIds] = useState<Set<string>>(new Set());
  const toastAlert = useToastAlert();
  const api = useApi();
  const navigate = useNavigate();
  const { aiConfigurations } = useAIConfigs();

  const VIDEO_DOMAINS = ["youtube.com", "youtu.be", "vimeo.com", "dailymotion.com", "twitch.tv", "tiktok.com"];
  const isVideoUrl = (url: string) => {
    const lower = url.toLowerCase();
    return VIDEO_DOMAINS.some(domain => lower.includes(domain));
  };
  const { site } = useSite();
  const { azureIndexes, add: addAzureIndex } = useAzureIndexes();
  const { publishJob } = useAzureIndexesApi();
  const jobsRef = useRef<ScraperJob[]>([]);

  // Filter jobs based on active tab (client-side filtering, no refetch)
  const jobs = React.useMemo(() => {
    if (activeTab === "web") {
      return allJobs.filter((job) => job.job_type === "web" || (!job.job_type && Number(job.total_urls) > 0));
    }
    if (activeTab === "video") {
      return allJobs.filter((job) => job.job_type === "video");
    }
    // file tab: file type, or legacy jobs with total_urls === 0 and no job_type
    return allJobs.filter((job) => job.job_type === "file" || (!job.job_type && Number(job.total_urls) === 0));
  }, [allJobs, activeTab]);

  // Keep ref in sync with ALL jobs for polling (must check all jobs regardless of active tab)
  jobsRef.current = allJobs;

  // Load integration sources on mount (and when switching to sources tab)
  const fetchIntegrationSources = useCallback(async () => {
    setSourcesLoading(true);
    try {
      const resp = await api.messaging.get("AzureIndexes/IntegrationSources");
      if (resp.ok) {
        const data = await resp.json();
        setIntegrationSources(Array.isArray(data?.sources) ? data.sources : []);
      }
    } catch { /* safe default */ }
    finally { setSourcesLoading(false); }
  }, [api]);

  const syncSource = useCallback(async (source: string) => {
    setSyncingSource(source);
    try {
      const resp = await api.messaging.post("AzureIndexes/SyncIntegration", { source });
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Sync started — delta detection active, only changed content re-embeds.");
        setTimeout(fetchIntegrationSources, 2000);
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to start sync.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to start sync.");
    } finally {
      setSyncingSource(null);
    }
  }, [api, toastAlert, fetchIntegrationSources]);

  React.useEffect(() => {
    if (activeTab === "sources") fetchIntegrationSources();
  }, [activeTab]);

  // Fetch headers for Python scraper API (JSON requests)
  const getHeaders = useCallback(() => {
    return {
      "Content-Type": "application/json",
      "X-Service-Key": SCRAPER_API_KEY,
      "X-Site-Id": site?.siteId?.toString() || "0"
    };
  }, [site?.siteId]);

  // Upload headers (no Content-Type - let browser set multipart boundary)
  const getUploadHeaders = useCallback(() => {
    return {
      "X-Service-Key": SCRAPER_API_KEY,
      "X-Site-Id": site?.siteId?.toString() || "0"
    };
  }, [site?.siteId]);

  // Fetch all jobs (no filtering - done client-side)
  const fetchJobs = useCallback(async () => {
    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs`, {
        headers: getHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAllJobs(data); // Store all jobs, filtering happens in useMemo
      } else {
        console.error("Failed to fetch jobs:", response.status);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  // Initial fetch on mount (no refetch on tab change - filtering is client-side)
  useEffect(() => {
    // Only fetch if site is loaded (siteId > 0)
    if (site?.siteId && site.siteId > 0) {
      setLoading(true);
      fetchJobs();
    } else {
      setLoading(false);
    }
  }, [site?.siteId, fetchJobs]);

  // Load M365 connection status + scraper quota once on mount
  useEffect(() => {
    api.admin.get("MicrosoftGraph/Status").then(res => res.json()).then(data => {
      setM365Connected(data?.connected === true);
    }).catch(() => setM365Connected(false));

    api.admin.get("ScraperJobs/Quota").then(res => res.json()).then(data => {
      setQuota(data);
    }).catch(() => {/* quota display is optional */});
  }, [api]);

  // Load conversation training status + insights when tab is active
  useEffect(() => {
    if (activeTab !== "conversation") return;
    setConvTrainingLoading(true);
    Promise.all([
      api.admin.get("ConversationTraining/Status").then(r => r.json()).catch(() => null),
      api.admin.get("ConversationTraining/Insights?days=30").then(r => r.json()).catch(() => null),
    ]).then(([status, insights]) => {
      setConvTrainingStatus(status ?? null);
      setConvInsights(insights ?? null);
    }).finally(() => setConvTrainingLoading(false));
  }, [activeTab, api]);

  // Load paginated conversation list when on conversation tab
  useEffect(() => {
    if (activeTab !== "conversation" || !convTrainingStatus?.enabled) return;
    api.admin.get(`ConversationTraining/Conversations?page=${convTrainingPage}&pageSize=20`)
      .then(res => res.json())
      .then(data => {
        setConvTrainingConvs(Array.isArray(data?.items) ? data.items : []);
        setConvTrainingTotal(data?.total ?? 0);
      })
      .catch(() => {
        setConvTrainingConvs([]);
        setConvTrainingTotal(0);
      });
  }, [activeTab, convTrainingStatus?.enabled, convTrainingPage, api]);

  const removeConversationFromTraining = async (conversationId: number) => {
    setConvTrainingRemoving(conversationId);
    try {
      const res = await api.admin.delete(`ConversationTraining/Conversation/${conversationId}`);
      if (res.ok) {
        toastAlert.displayToast(Severity.Success, "Conversation removed from training index");
        setConvTrainingConvs(prev => prev.filter(c => c.conversationId !== conversationId));
        setConvTrainingTotal(prev => Math.max(0, prev - 1));
        setConvTrainingStatus(prev => prev ? { ...prev, totalIndexed: Math.max(0, prev.totalIndexed - 1) } : prev);
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to remove conversation");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to remove conversation");
    } finally {
      setConvTrainingRemoving(null);
    }
  };

  // Polling for running jobs - uses ref to avoid stale closure and infinite loop
  useEffect(() => {
    const interval = setInterval(() => {
      const hasRunningJobs = jobsRef.current.some(
        j => j.status === "running" || j.status === "pending"
      );
      if (hasRunningJobs) {
        fetchJobs();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchJobs]);

  // Batch extract transcripts from channel search results
  const extractTranscripts = async (videos: { url: string; name: string }[]) => {
    let successCount = 0;
    let errorCount = 0;

    for (const video of videos) {
      try {
        const response = await fetch(`${SCRAPER_API_URL}/scrape-video`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify({ url: video.url, name: video.name, language: "en" })
        });
        if (response.ok) {
          const data = await response.json();
          if (data.status === "completed") {
            successCount++;
          } else {
            errorCount++;
          }
        } else {
          errorCount++;
        }
      } catch {
        errorCount++;
      }
    }

    if (successCount > 0) {
      toastAlert.displayToast(
        Severity.Success,
        `Extracted transcripts for ${successCount} video${successCount !== 1 ? "s" : ""}`
      );
    }
    if (errorCount > 0) {
      toastAlert.displayToast(
        Severity.Error,
        `Failed to extract ${errorCount} video${errorCount !== 1 ? "s" : ""}`
      );
    }
    fetchJobs();
  };

  // Create new scraper job — routes through admin proxy for quota enforcement
  const createJob = async (request: CreateScraperJobRequest) => {
    try {
      const response = await api.admin.post("ScraperJobs/Create", request);
      const data = await response.json();

      // Quota check: admin proxy returns { allowed: false, reason: "..." } when limit hit
      if (data.allowed === false) {
        toastAlert.displayToast(Severity.Warning, data.reason || "Job limit reached");
        return;
      }

      if (response.ok && data.job_id) {
        toastAlert.displayToast(Severity.Success, `Job "${data.name}" created successfully`);
        setShowCreateModal(false);
        fetchJobs();
        // Refresh quota display
        api.admin.get("ScraperJobs/Quota").then(r => r.json()).then(setQuota).catch(() => {});
      } else {
        toastAlert.displayToast(Severity.Error, data.detail || data.error || "Failed to create job");
      }
    } catch (error) {
      console.error("Error creating job:", error);
      toastAlert.displayToast(Severity.Error, "Failed to create scraper job");
    }
  };

  // View job results
  const viewResults = async (job: ScraperJob) => {
    if (job.status !== "completed" && job.status !== "failed") {
      toastAlert.displayToast(Severity.Warning, "Job is still in progress");
      return;
    }

    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}/results`, {
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedJob(job);
        setJobResults(data.results);
        setShowResultsModal(true);
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to fetch results");
      }
    } catch (error) {
      console.error("Error fetching results:", error);
      toastAlert.displayToast(Severity.Error, "Failed to fetch job results");
    }
  };

  // Delete job
  const deleteJob = async (job: ScraperJob) => {
    if (!confirm(`Are you sure you want to delete job "${job.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}`, {
        method: "DELETE",
        headers: getHeaders()
      });

      if (response.ok) {
        toastAlert.displayToast(Severity.Success, `Job "${job.name}" deleted`);
        fetchJobs();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to delete job");
      }
    } catch (error) {
      console.error("Error deleting job:", error);
      toastAlert.displayToast(Severity.Error, "Failed to delete job");
    }
  };

  // Upload files to scraper service (sequential to avoid race conditions)
  const uploadFiles = async () => {
    if (newFiles.length === 0) return;

    setUploading(true);
    // Mark all files as pending
    const initial: Record<string, "pending" | "uploading" | "done" | "error"> = {};
    newFiles.forEach(f => { initial[f.name] = "pending"; });
    setUploadProgress(initial);

    let successCount = 0;
    let errorCount = 0;

    // Upload sequentially to avoid scraper service race conditions
    for (const file of newFiles) {
      setUploadProgress(prev => ({ ...prev, [file.name]: "uploading" }));

      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);
      formData.append("extract_images", extractPdfImages.toString());

      try {
        const response = await fetch(`${SCRAPER_API_URL}/upload`, {
          method: "POST",
          headers: getUploadHeaders(),
          body: formData
        });

        if (response.ok) {
          setUploadProgress(prev => ({ ...prev, [file.name]: "done" }));
          successCount++;
        } else {
          const error = await response.json().catch(() => ({}));
          console.error(`Failed to upload ${file.name}:`, error);
          setUploadProgress(prev => ({ ...prev, [file.name]: "error" }));
          errorCount++;
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        setUploadProgress(prev => ({ ...prev, [file.name]: "error" }));
        errorCount++;
      }
    }

    setUploading(false);
    setNewFiles([]);
    setTimeout(() => setUploadProgress({}), 3000);

    if (successCount > 0) {
      toastAlert.displayToast(
        Severity.Success,
        `Successfully uploaded ${successCount} file(s). Processing — check the job list below.`
      );
      fetchJobs();
    }

    if (errorCount > 0) {
      toastAlert.displayToast(
        Severity.Error,
        `Failed to upload ${errorCount} file(s)`
      );
    }
  };

  // View extracted content from file
  const viewFileContent = async (job: ScraperJob) => {
    if (job.status !== "completed") {
      toastAlert.displayToast(Severity.Warning, "File is still processing");
      return;
    }

    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}/results`, {
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.results && data.results[0]) {
          const content = data.results[0].content;
          // Escape HTML to prevent XSS
          const escapeHtml = (text: string) => {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
          };
          const win = window.open("", "_blank");
          if (win) {
            win.document.write(`
              <html>
                <head>
                  <title>${escapeHtml(job.name)} - Extracted Content</title>
                  <style>
                    body { font-family: Arial, sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; }
                    pre { white-space: pre-wrap; word-wrap: break-word; background: #f5f5f5; padding: 15px; border-radius: 5px; }
                  </style>
                </head>
                <body>
                  <h1>${escapeHtml(job.name)}</h1>
                  <p><strong>Extracted Content:</strong></p>
                  <pre>${escapeHtml(content)}</pre>
                </body>
              </html>
            `);
            win.document.close();
          }
        }
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to fetch content");
      }
    } catch (error) {
      console.error("Error fetching content:", error);
      toastAlert.displayToast(Severity.Error, "Failed to fetch content");
    }
  };

  // Push completed job results into the unified KB Azure Search index
  const indexToKbSearch = async (job: ScraperJob) => {
    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}/results`, {
        headers: getHeaders()
      });
      if (!response.ok) {
        toastAlert.displayToast(Severity.Error, "Failed to fetch results for KB indexing");
        return;
      }
      const data = await response.json();
      const pages = Array.isArray(data.results) ? data.results : [];
      if (pages.length === 0) {
        toastAlert.displayToast(Severity.Warning, "No results to index");
        return;
      }

      const indexRes = await api.admin.post("kb/search/index-scraper", {
        jobId: job.job_id,
        jobName: job.name,
        pages: pages.map((p: ScrapedPage) => ({
          url: p.url,
          title: p.title || job.name,
          description: "",
          content: p.content
        }))
      });
      const indexData = await indexRes.json();
      if (indexRes.ok) {
        toastAlert.displayToast(Severity.Success, `Indexed ${indexData.indexed ?? pages.length} page(s) to KB Search`);
      } else {
        toastAlert.displayToast(Severity.Error, indexData.error || "KB indexing failed");
      }
    } catch (error) {
      console.error("Error indexing to KB search:", error);
      toastAlert.displayToast(Severity.Error, "Failed to index to KB Search");
    }
  };

  // Export scraper results to SharePoint
  const exportToSharePoint = async (job: ScraperJob) => {
    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}/results`, {
        headers: getHeaders()
      });
      if (!response.ok) {
        toastAlert.displayToast(Severity.Error, "Failed to fetch job results for export");
        return;
      }
      const data = await response.json();
      setSpExportJob(job);
      setSpExportResults(Array.isArray(data.results) ? data.results : []);
      setShowSharePointExportModal(true);
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to fetch job results");
    }
  };

  // Cancel (stop) a running or pending job
  const cancelJob = async (job: ScraperJob) => {
    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}/cancel`, {
        method: "POST",
        headers: getHeaders()
      });

      if (response.ok) {
        toastAlert.displayToast(Severity.Success, `Job "${job.name}" stopped`);
        fetchJobs();
      } else {
        const error = await response.json().catch(() => ({}));
        toastAlert.displayToast(Severity.Error, error.detail || "Failed to stop job");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to stop job");
    }
  };

  // Resume job
  const resumeJob = async (job: ScraperJob) => {
    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}/resume`, {
        method: "POST",
        headers: getHeaders()
      });

      if (response.ok) {
        toastAlert.displayToast(Severity.Success, `Job "${job.name}" resumed`);
        fetchJobs();
      } else {
        const error = await response.json();
        toastAlert.displayToast(Severity.Error, error.detail || "Failed to resume job");
      }
    } catch (error) {
      console.error("Error resuming job:", error);
      toastAlert.displayToast(Severity.Error, "Failed to resume job");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("sources")}
            className={`${
              activeTab === "sources"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5`}
          >
            All Sources
            {integrationSources.length > 0 && (
              <span className="text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                {integrationSources.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("web")}
            className={`${
              activeTab === "web"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Web Training
          </button>
          <button
            onClick={() => setActiveTab("file")}
            className={`${
              activeTab === "file"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            File Training
          </button>
          <button
            onClick={() => setActiveTab("video")}
            className={`${
              activeTab === "video"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Video Training
          </button>
          <button
            onClick={() => setActiveTab("sharepoint")}
            className={`${
              activeTab === "sharepoint"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5`}
          >
            SharePoint & OneDrive
            {m365Connected && (
              <span className="text-[10px] font-medium bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Connected</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("json")}
            className={`${
              activeTab === "json"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            JSON Import
          </button>
          <button
            onClick={() => setActiveTab("conversation")}
            className={`${
              activeTab === "conversation"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5`}
          >
            Conversation Training
            {convTrainingStatus?.enabled && (
              <span className="text-[10px] font-medium bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                {convTrainingStatus.totalIndexed} indexed
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6 gap-4">
        <div className="flex-1">
          <h2 className="text-xl font-semibold">
            {activeTab === "sources" ? "All Knowledge Sources"
              : activeTab === "web" ? "Web Scraper Jobs"
              : activeTab === "video" ? "Video Training"
              : activeTab === "sharepoint" ? "SharePoint & OneDrive"
              : activeTab === "conversation" ? "Conversation Training"
              : activeTab === "json" ? "JSON Import"
              : "File Training"}
          </h2>
          <p className="text-sm text-slate-500">
            {activeTab === "sources"
              ? "All indexed knowledge sources for this bot — integrations, scraper, KB articles, and training data"
              : activeTab === "web"
              ? "Create and manage web scraping jobs for AI training data"
              : activeTab === "video"
              ? "Extract transcripts from video URLs for AI training"
              : activeTab === "sharepoint"
              ? "Browse your SharePoint libraries and OneDrive for Business drives to import documents into the AI training pipeline"
              : activeTab === "conversation"
              ? "Let your bot learn from real agent conversations — gets smarter automatically with every resolved chat"
              : activeTab === "json"
              ? "Import any JSON file directly into the bot index — scrapeyscrape exports, Q&A arrays, or custom data"
              : "Upload and process files for AI training"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate("/ai-new/chat")}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
            title="Test raw ingestion — no persona or workflows"
          >
            Test Ingestion
          </button>
          {aiConfigurations.length > 0 && (
            <button
              onClick={() => navigate(`/ai-new/settings/test-chat/${aiConfigurations[0].id}`)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              title="Preview the full bot with persona and workflows"
            >
              Preview Bot
            </button>
          )}
          {activeTab === "web" && (
            <PrimaryBlueButton
              label="New Scraper Job"
              onClick={() => setShowCreateModal(true)}
            />
          )}
        </div>
      </div>

      {/* Sources tab — all auto-managed knowledge indexes */}
      {activeTab === "sources" && (
        <div className="space-y-3">
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900 mb-2">
            <p className="font-semibold mb-1">All knowledge sources</p>
            <p>All sources feed into your bot's AI search index. Integration sources (BigCommerce, Shopify, KB articles) sync automatically. Web scrape jobs and file uploads are triggered manually. Only changed content is re-embedded on each sync.</p>
          </div>
          {sourcesLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : integrationSources.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No indexed sources yet. Connect an integration or run a scraper job to get started.</div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 divide-y">
              {integrationSources.map((src) => {
                const meta = SOURCE_META[src.sourceType] ?? { label: src.sourceType, description: "", icon: "📦" };
                return (
                  <div key={src.sourceType} className="px-5 py-4 flex items-center gap-4">
                    <span className="text-2xl flex-shrink-0" aria-hidden="true">{meta.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-slate-800">{meta.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{meta.description}</div>
                      <div className="flex flex-wrap gap-4 mt-1.5 text-xs text-slate-600">
                        <span><span className="font-medium">{src.documentCount.toLocaleString()}</span> documents</span>
                        <span><span className="font-medium">{src.totalChunks.toLocaleString()}</span> chunks</span>
                        {src.lastIndexedAt && (
                          <span className="text-slate-400">Last synced: {new Date(src.lastIndexedAt).toLocaleString()}</span>
                        )}
                        {src.hasErrors && (
                          <span className="text-amber-600 font-medium">⚠ Some errors</span>
                        )}
                      </div>
                    </div>
                    {meta.syncKey && (
                      <button
                        type="button"
                        disabled={syncingSource === meta.syncKey}
                        onClick={() => syncSource(meta.syncKey!)}
                        className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          syncingSource === meta.syncKey
                            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      >
                        {syncingSource === meta.syncKey ? "Syncing..." : "Sync Now"}
                      </button>
                    )}
                    {!meta.syncKey && meta.tabKey && (
                      <button
                        type="button"
                        onClick={() => setActiveTab(meta.tabKey!)}
                        className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                      >
                        {meta.tabKey === "web" ? "Re-scrape →" : "Add More →"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {integrationSources.length > 0 && (
            <button
              type="button"
              onClick={fetchIntegrationSources}
              className="text-xs text-slate-400 hover:text-slate-600 mt-1"
            >
              Refresh
            </button>
          )}
        </div>
      )}

      {/* Web tab: explanatory card + usage banner */}
      {activeTab === "web" && (
        <>
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 mb-4 text-sm text-blue-900">
            <p className="font-semibold mb-1">What is the Web Scraper?</p>
            <p className="text-blue-800 text-xs leading-relaxed">
              The web scraper crawls your websites and extracts text content to train your AI chatbot.
              Scraped data can be published directly to an AI Search Index, or exported to SharePoint
              as a compliance record — giving you a full audit trail of exactly what your AI was trained on.
              Usage is metered per your plan. Additional quota is available on request.
            </p>
          </div>
          <ScraperUsageBanner quota={quota} />
        </>
      )}

      {/* YouTube Channel Search */}
      {activeTab === "video" && (
        <YouTubeChannelSearch
          scraperApiUrl={SCRAPER_API_URL}
          getHeaders={getHeaders}
          onExtractTranscripts={extractTranscripts}
        />
      )}

      {/* Video URL Input Section */}
      {activeTab === "video" && (
        <div className="mb-6 bg-white rounded-lg border p-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Video URL</label>
              <input
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-slate-400 mt-1">
                Supports YouTube, Vimeo, Dailymotion, and many other video platforms. The video must have captions or subtitles available.
              </p>
              {videoUrl.trim() && !isVideoUrl(videoUrl.trim()) && (
                <p className="text-xs text-amber-600 mt-1">
                  If this is a regular webpage (not a video), use the Web Training tab instead. Video extraction requires the URL to host video content with captions/subtitles.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={videoName}
                onChange={(e) => setVideoName(e.target.value)}
                placeholder="Give this video a name for reference"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end">
              <PrimaryBlueButton
                label={videoSubmitting ? "Extracting..." : "Extract Transcript"}
                disabled={videoSubmitting || !videoUrl.trim() || !videoName.trim()}
                onClick={async () => {
                  setVideoSubmitting(true);
                  try {
                    const response = await fetch(`${SCRAPER_API_URL}/scrape-video`, {
                      method: "POST",
                      headers: getHeaders(),
                      body: JSON.stringify({
                        url: videoUrl.trim(),
                        name: videoName.trim(),
                        language: "en"
                      })
                    });
                    if (response.ok) {
                      const data = await response.json();
                      if (data.status === "completed") {
                        toastAlert.displayToast(Severity.Success, data.message || "Transcript extracted successfully");
                      } else {
                        toastAlert.displayToast(Severity.Error, data.message || "Failed to extract transcript");
                      }
                      setVideoUrl("");
                      setVideoName("");
                      fetchJobs();
                    } else {
                      const error = await response.json();
                      toastAlert.displayToast(Severity.Error, error.detail || "Failed to extract transcript");
                    }
                  } catch (error) {
                    console.error("Error scraping video:", error);
                    toastAlert.displayToast(Severity.Error, "Failed to extract video transcript");
                  } finally {
                    setVideoSubmitting(false);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* File Upload Section */}
      {activeTab === "file" && (
        <div className="mb-6">
          <FileUpload onAddFiles={setNewFiles} multiple accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.json,.txt,.jpg,.jpeg,.png">
            <div className="flex flex-row text-slate-500 text-xs">
              Supported files: PDF, DOCX, DOC, XLSX, XLS, CSV, JSON, TXT, JPG, JPEG, PNG
            </div>
            <div className="flex flex-row text-slate-500 text-xs">
              Files will be processed and text content extracted for AI training
            </div>
          </FileUpload>
          <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-600">
            <span className="font-medium text-slate-700">Best formats for AI accuracy:</span>{" "}
            <span className="text-green-700 font-medium">TXT / Markdown</span> — best; headings and paragraphs create clean chunks.{" "}
            <span className="text-blue-700 font-medium">PDF / DOCX</span> — great for existing docs.{" "}
            <span className="text-amber-700 font-medium">JSON</span> — works if flat (array of {"{"}question, answer{"}"} or {"{"}title, content{"}"} objects). Complex nested JSON may produce poor results.{" "}
            <span className="text-slate-500">CSV / XLSX</span> — each row becomes a chunk; keep rows concise.
          </div>
          {newFiles.some(f => f.name.toLowerCase().endsWith(".pdf")) && (
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={extractPdfImages}
                onChange={e => setExtractPdfImages(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span>Extract images from PDF files</span>
              <span className="text-xs text-slate-400">(adds embedded images to AI training — takes slightly longer)</span>
            </label>
          )}
          <FilesEditor files={newFiles} onUpdate={setNewFiles} />
          {Object.keys(uploadProgress).length > 0 && (
            <div className="mt-3 space-y-1">
              {Object.entries(uploadProgress).map(([name, status]) => (
                <div key={name} className="flex items-center gap-2 text-xs">
                  {status === "pending" && <span className="text-slate-400">○</span>}
                  {status === "uploading" && <span className="text-blue-500 animate-pulse">↑</span>}
                  {status === "done" && <span className="text-green-600">✓</span>}
                  {status === "error" && <span className="text-red-500">✗</span>}
                  <span className={`truncate max-w-xs ${status === "error" ? "text-red-600" : status === "done" ? "text-green-700" : "text-slate-600"}`}>{name}</span>
                  <span className="text-slate-400">{status === "uploading" ? "uploading..." : status === "done" ? "uploaded" : status === "error" ? "failed" : "queued"}</span>
                </div>
              ))}
            </div>
          )}
          {newFiles.length > 0 && (
            <div className="flex justify-end mt-4">
              <PrimaryBlueButton
                label={uploading ? `Uploading ${Object.values(uploadProgress).filter(s => s === "done" || s === "error").length}/${Object.keys(uploadProgress).length}...` : "Upload"}
                disabled={uploading}
                onClick={uploadFiles}
              />
            </div>
          )}
        </div>
      )}

      {/* JSON Import Tab */}
      {activeTab === "json" && (
        <div className="mb-6 space-y-4">
          {/* Info callout */}
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <p className="font-semibold mb-1">Accepted formats</p>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
              <li><span className="font-medium">Scrapeyscrape job export</span> — the <code className="bg-blue-100 px-1 rounded">{"{"}"job_id", "results":[...]{"}"}</code> JSON downloaded from a completed job</li>
              <li><span className="font-medium">Flat page array</span> — <code className="bg-blue-100 px-1 rounded">{"[{"}"url","title","text"{"}"}, ...]</code> — any field named <code className="bg-blue-100 px-1 rounded">content</code>, <code className="bg-blue-100 px-1 rounded">content_cleaned</code>, or <code className="bg-blue-100 px-1 rounded">body</code> also works</li>
              <li><span className="font-medium">Q&amp;A pairs</span> — <code className="bg-blue-100 px-1 rounded">{"[{"}"question","answer"{"}"}, ...]</code></li>
              <li><span className="font-medium">Native format</span> — <code className="bg-blue-100 px-1 rounded">{"[{"}"contentId","title","text","sourceUrl"{"}"}, ...]</code></li>
            </ul>
            <p className="text-xs text-blue-700 mt-2 font-medium">Scrapeyscrape settings for best results: default extraction, no AI summary, export as JSON after job completes.</p>
          </div>

          {/* File picker */}
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">Select JSON file</label>
            <input
              type="file"
              accept=".json"
              onChange={(e) => {
                setJsonImportFile(e.target.files?.[0] ?? null);
                setJsonImportResult(null);
              }}
              className="block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {jsonImportFile && (
              <p className="text-xs text-slate-500 mt-1">{jsonImportFile.name} — {(jsonImportFile.size / 1024).toFixed(1)} KB</p>
            )}
          </div>

          {/* Import button */}
          {jsonImportFile && !jsonImportResult && (
            <div className="flex justify-end">
              <PrimaryBlueButton
                label={jsonImporting ? "Importing..." : "Import & Index"}
                disabled={jsonImporting}
                onClick={async () => {
                  setJsonImporting(true);
                  try {
                    const form = new FormData();
                    form.append("file", jsonImportFile);
                    const r = await api.messaging.postFormData("scraper/import-json", form);
                    if (r.ok) {
                      const data: JsonImportResult = await r.json();
                      setJsonImportResult(data);
                      if (data.errors === 0) {
                        toastAlert.displayToast(Severity.Success, `Imported ${data.pagesProcessed} pages — ${data.chunksCreated} chunks indexed.`);
                      } else {
                        toastAlert.displayToast(Severity.Warning, `${data.pagesProcessed - data.errors} pages imported, ${data.errors} failed.`);
                      }
                    } else {
                      const err = await r.json().catch(() => ({ error: "Import failed" }));
                      toastAlert.displayToast(Severity.Error, err.error ?? "Import failed");
                    }
                  } catch (e) {
                    console.error("JSON import error:", e);
                    toastAlert.displayToast(Severity.Error, "Import failed — check console for details");
                  } finally {
                    setJsonImporting(false);
                  }
                }}
              />
            </div>
          )}

          {/* Results */}
          {jsonImportResult && (
            <div className="bg-white rounded-lg border border-slate-200 divide-y text-sm">
              <div className="px-4 py-3 flex gap-6 text-xs font-medium text-slate-600">
                <span><span className="text-slate-900 font-semibold">{jsonImportResult.pagesProcessed}</span> pages</span>
                <span><span className="text-green-700 font-semibold">{jsonImportResult.chunksCreated}</span> chunks indexed</span>
                {jsonImportResult.skipped > 0 && <span><span className="text-amber-600 font-semibold">{jsonImportResult.skipped}</span> unchanged (skipped)</span>}
                {jsonImportResult.errors > 0 && <span><span className="text-red-600 font-semibold">{jsonImportResult.errors}</span> errors</span>}
              </div>
              <div className="max-h-64 overflow-y-auto divide-y">
                {jsonImportResult.pages.map((p, i) => (
                  <div key={i} className="px-4 py-2 flex items-start gap-3 text-xs">
                    <span className={p.status === "ok" ? "text-green-600 mt-0.5" : "text-red-500 mt-0.5"}>
                      {p.status === "ok" ? "✓" : "✗"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-slate-700 font-medium">{p.title || p.contentId}</div>
                      {p.status === "ok" && p.chunks > 0 && <div className="text-slate-400">{p.chunks} chunks</div>}
                      {p.status === "ok" && p.chunks === 0 && <div className="text-amber-600">unchanged</div>}
                      {p.error && <div className="text-red-600">{p.error}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2">
                <button
                  type="button"
                  onClick={() => { setJsonImportResult(null); setJsonImportFile(null); }}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Import another file
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SharePoint Tab */}
      {activeTab === "sharepoint" && (
        <div className="mb-6">
          {m365Connected ? (
            <SharePointFileBrowser
              scraperApiUrl={SCRAPER_API_URL}
              scraperHeaders={getUploadHeaders()}
              onImportComplete={() => {
                setActiveTab("file");
                fetchJobs();
              }}
            />
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-5 text-sm text-amber-800">
              <p className="font-semibold mb-1">Microsoft 365 not connected</p>
              <p className="text-xs mb-3">
                Connect your Microsoft 365 account to browse SharePoint libraries and OneDrive for Business drives to import files for AI training.
                It takes about 30 seconds — just click Connect and approve on Microsoft's screen.
              </p>
              <Link to="/integrations/microsoft-365" className="inline-block text-xs font-medium text-blue-700 underline">
                Go to Integrations → Microsoft 365 → Connect
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Conversation Training Tab */}
      {activeTab === "conversation" && (
        <div>
          {/* How it works — expandable accordion */}
          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 mb-4">
            <button
              onClick={() => setConvTrainingExpanded(!convTrainingExpanded)}
              className="flex items-center justify-between w-full text-sm font-semibold text-blue-900"
              aria-expanded={convTrainingExpanded}
              aria-controls="conv-training-explainer"
            >
              <span>How Conversation Training Works</span>
              <svg
                className={`w-4 h-4 text-blue-600 transition-transform ${convTrainingExpanded ? "rotate-180" : ""}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {convTrainingExpanded && (
              <div id="conv-training-explainer" className="mt-3 text-xs text-blue-800 space-y-3">
                <div>
                  <p className="font-semibold mb-1">What it does</p>
                  <p className="leading-relaxed">
                    Every time one of your agents resolves a conversation, Velaro automatically extracts the Q&amp;A pairs
                    — visitor questions paired with agent answers — and indexes them into the AI search index. The next time
                    a visitor asks a similar question, the bot retrieves your agents' best answers as context and uses them
                    to craft a response. <strong>The bot gets smarter on its own, with zero effort from you.</strong>
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">How it combines with KB Articles &amp; Web Training</p>
                  <p className="leading-relaxed">
                    All three sources — KB Articles, Web Training, and Conversation Training — live in the same search index.
                    When a visitor asks a question, the AI searches all sources simultaneously and picks the most semantically
                    relevant answer, regardless of where it came from. More sources = more coverage = better answers.
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Quality filter — CSAT &amp; conversation length</p>
                  <p className="leading-relaxed">
                    Only <em>good</em> conversations are indexed. To qualify, a conversation must have at least 4 messages
                    (2 visitor + 2 agent). If your customer rated the conversation with CSAT, only scores of 4 or 5 stars
                    are indexed — your bot won't learn from poor experiences. Conversations without CSAT are included
                    (most chats don't collect it). Enable CSAT or thumbs-up ratings to sharpen the quality filter over time.
                  </p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Why this beats competitors</p>
                  <p className="leading-relaxed">
                    Most AI chatbots are trained once at setup and go stale. Velaro's Conversation Training is <em>continuous</em>
                    — every good conversation makes the bot slightly smarter, automatically. After 30 days you'll have a bot
                    that knows the real answers your customers actually ask, in the exact language they use. This is the same
                    RAG (Retrieval-Augmented Generation) architecture used by enterprise AI systems, now available in your bot
                    without any ML expertise required.
                  </p>
                </div>

              </div>
            )}
          </div>

          {/* Vector search not configured */}
          {!convTrainingLoading && convTrainingStatus?.enabled && convTrainingStatus?.vectorReady === false && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-4 text-sm text-amber-800">
              <p className="font-semibold mb-1">Azure AI Search not configured</p>
              <p className="text-xs">The vector index service is not connected. Contact Velaro support to complete the setup — conversations are being received but cannot be indexed until this is configured.</p>
            </div>
          )}

          {/* Status */}
          {convTrainingLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : !convTrainingStatus?.enabled ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-5 text-sm text-amber-800">
              <p className="font-semibold mb-2">Conversation Training is not included in your current plan</p>
              <p className="text-xs mb-3">
                Upgrade your plan to enable automatic conversation indexing — every resolved conversation makes your bot smarter.
              </p>
              {/* TODO(billing): Replace button with <UpgradeButton feature="Conversation Training" plan="Pro" /> → Stripe Checkout. See docs/self-serve-billing-todo.md */}
              <button
                onClick={() => {}}
                className="text-xs font-medium text-amber-900 underline hover:text-amber-700"
              >
                Upgrade your plan →
              </button>
            </div>
          ) : (
            <>
              {/* Stats row */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-white rounded-lg border p-4 text-center">
                  <div className="text-3xl font-bold text-purple-600">{convTrainingStatus.totalIndexed}</div>
                  <div className="text-xs text-slate-500 mt-1">Total Indexed</div>
                </div>
                {convTrainingStatus.byChannel?.slice(0, 2).map(ch => (
                  <div key={ch.channel} className="bg-white rounded-lg border p-4 text-center">
                    <div className="text-3xl font-bold text-blue-600">{ch.count}</div>
                    <div className="text-xs text-slate-500 mt-1 capitalize">{ch.channel}</div>
                  </div>
                ))}
              </div>
              {/* Monthly usage */}
              {convTrainingStatus.thisMonth !== undefined && (
                <div className="flex gap-4 text-xs text-slate-500 mb-4">
                  <span><span className="font-medium text-slate-700">{convTrainingStatus.thisMonth}</span> indexed this month</span>
                  {!convTrainingStatus.unlimited && convTrainingStatus.monthlyLimit && (
                    <span className="text-slate-400">/ {convTrainingStatus.monthlyLimit} limit</span>
                  )}
                  {convInsights && convInsights.total > 0 && (
                    <span className="text-slate-400">{convInsights.indexedRate}% index rate (last 30 days)</span>
                  )}
                </div>
              )}

              {/* Indexed conversations table */}
              {convTrainingTotal === 0 ? (
                <div className="space-y-4">
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <p className="text-slate-500 mb-1">No conversations indexed yet</p>
                    <p className="text-xs text-slate-400">
                      Conversations are indexed automatically when they resolve. Check back after your next chat is closed.
                    </p>
                  </div>

                  {/* Diagnostics — show what's actually happening */}
                  {convInsights && (
                    <div className="rounded-lg border border-slate-200 bg-white">
                      <div className="px-4 py-3 border-b flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Activity — last 30 days</span>
                        {convInsights.total === 0 && (
                          <span className="text-xs text-amber-600 font-medium">⚠ No events recorded</span>
                        )}
                      </div>
                      {convInsights.total === 0 ? (
                        <div className="px-4 py-4 text-xs text-slate-500 space-y-1">
                          <p>No conversation events have been received in the last 30 days.</p>
                          <p className="text-slate-400">This usually means the messaging platform hasn't resolved any conversations yet, or the service-to-service connection needs to be verified. Close a test chat to trigger indexing.</p>
                        </div>
                      ) : (
                        <div className="px-4 py-3 space-y-3">
                          <div className="flex gap-6 text-xs">
                            <div>
                              <span className="font-semibold text-slate-800">{convInsights.total}</span>
                              <span className="text-slate-500 ml-1">conversations processed</span>
                            </div>
                            <div>
                              <span className="font-semibold text-green-700">{convInsights.indexed}</span>
                              <span className="text-slate-500 ml-1">indexed</span>
                            </div>
                            <div>
                              <span className="font-semibold text-amber-600">{convInsights.skipped}</span>
                              <span className="text-slate-500 ml-1">skipped</span>
                            </div>
                          </div>
                          {convInsights.byEvent.length > 0 && (
                            <div className="text-xs space-y-1">
                              <p className="text-slate-500 font-medium">Breakdown by outcome:</p>
                              {convInsights.byEvent.map(e => (
                                <div key={e.event} className="flex justify-between text-slate-600">
                                  <span className="capitalize">{e.event.replace(/_/g, " ")}</span>
                                  <span className="font-medium">{e.count}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {convInsights.recentSkips.length > 0 && (
                            <div className="text-xs space-y-1 border-t pt-3">
                              <p className="text-slate-500 font-medium">Recent skips:</p>
                              {convInsights.recentSkips.slice(0, 5).map((s, i) => (
                                <div key={i} className="flex gap-2 text-slate-600">
                                  <span className="text-slate-400 shrink-0">#{s.conversationId}</span>
                                  <span className="text-amber-700 capitalize">{s.event.replace(/_/g, " ")}</span>
                                  {s.detail && <span className="text-slate-400 truncate">{s.detail}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg border">
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <span className="text-sm font-medium text-slate-700">Indexed Conversations</span>
                    <span className="text-xs text-slate-500">{convTrainingTotal} total</span>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Conversation ID</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Channel</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">CSAT</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Chunks</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Indexed</th>
                          <th className="text-right p-3 text-xs font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {convTrainingConvs.map(conv => (
                          <tr key={conv.conversationId} className="hover:bg-slate-50">
                            <td className="p-3 text-sm text-slate-700">#{conv.conversationId}</td>
                            <td className="p-3">
                              <span className="capitalize text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full">
                                {conv.channel ?? "chat"}
                              </span>
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {conv.csatScore != null ? `${conv.csatScore} / 5` : <span className="text-slate-400">—</span>}
                            </td>
                            <td className="p-3 text-sm text-slate-600">{conv.chunksIndexed}</td>
                            <td className="p-3 text-xs text-slate-500">
                              {new Date(conv.indexedAt).toLocaleDateString()}
                            </td>
                            <td className="p-3 text-right">
                              <button
                                onClick={() => removeConversationFromTraining(conv.conversationId)}
                                disabled={convTrainingRemoving === conv.conversationId}
                                className="px-3 py-1 text-xs text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                              >
                                {convTrainingRemoving === conv.conversationId ? "Removing…" : "Remove"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {convTrainingTotal > 20 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <button
                        onClick={() => setConvTrainingPage(p => Math.max(1, p - 1))}
                        disabled={convTrainingPage === 1}
                        className="px-3 py-1 text-xs text-slate-600 border rounded hover:bg-slate-50 disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-slate-500">
                        Page {convTrainingPage} of {Math.ceil(convTrainingTotal / 20)}
                      </span>
                      <button
                        onClick={() => setConvTrainingPage(p => p + 1)}
                        disabled={convTrainingPage >= Math.ceil(convTrainingTotal / 20)}
                        className="px-3 py-1 text-xs text-slate-600 border rounded hover:bg-slate-50 disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Jobs List (web / file / video tabs only) */}
      {activeTab !== "sharepoint" && activeTab !== "conversation" && (jobs.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <p className="text-slate-500 mb-4">
            {activeTab === "web" ? "No scraper jobs yet" : activeTab === "video" ? "No video transcripts yet" : "No files uploaded yet"}
          </p>
          {activeTab === "web" && (
            <PrimaryBlueButton
              label="Create Your First Job"
              onClick={() => setShowCreateModal(true)}
            />
          )}
        </div>
      ) : activeTab === "video" ? (
        // Video Training Table
        <div className="bg-white rounded-lg border">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Name</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Created</th>
                <th className="text-right p-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {jobs.map((job) => (
                <tr key={job.job_id} className="hover:bg-slate-50">
                  <td className="p-3">
                    <span className="text-sm font-medium">{job.name}</span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      job.status === "completed" ? "bg-green-100 text-green-800" :
                      job.status === "failed" ? "bg-red-100 text-red-800" :
                      "bg-blue-100 text-blue-800"
                    }`}>
                      {job.status}
                    </span>
                    {job.status === "failed" && job.error && (
                      <span className="ml-2 text-xs text-red-500" title={job.error}>
                        {job.error.length > 60 ? job.error.substring(0, 60) + "..." : job.error}
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-slate-600">
                      {new Date(job.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2 flex-wrap">
                      {job.status === "completed" && (
                        <>
                          <button
                            onClick={() => viewFileContent(job)}
                            className={rowPill.blue}
                          >
                            📄 Transcript
                          </button>
                          <button
                            onClick={() => {
                              setPublishingJob(job);
                              setShowPublishModal(true);
                            }}
                            className={rowPill.green}
                          >
                            ↑ Publish
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => deleteJob(job)}
                        className={rowPill.red}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : activeTab === "file" ? (
        // File Training Table
        <div className="bg-white rounded-lg border">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-slate-600">File Name</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Uploaded</th>
                <th className="text-right p-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {jobs.map((job) => (
                <tr key={job.job_id} className="hover:bg-slate-50">
                  <td className="p-3">
                    <span className="text-sm font-medium">{job.name}</span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      job.status === "completed" ? "bg-green-100 text-green-800" :
                      job.status === "failed" ? "bg-red-100 text-red-800" :
                      "bg-blue-100 text-blue-800"
                    }`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-slate-600">
                      {new Date(job.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2 flex-wrap">
                      {job.status === "completed" && (
                        <>
                          <button
                            onClick={() => viewFileContent(job)}
                            className={rowPill.blue}
                          >
                            📄 View Content
                          </button>
                          <button
                            onClick={() => {
                              setPublishingJob(job);
                              setShowPublishModal(true);
                            }}
                            className={rowPill.green}
                          >
                            ↑ Publish
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => deleteJob(job)}
                        className={rowPill.red}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Web Training Table
        <div className="bg-white rounded-lg border">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Name</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Progress</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Scraped</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Created</th>
                <th className="text-right p-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <ScraperJobRow
                  key={job.job_id}
                  job={job}
                  onViewResults={() => viewResults(job)}
                  onDelete={() => deleteJob(job)}
                  onResume={() => resumeJob(job)}
                  onStop={() => cancelJob(job)}
                  isPublished={publishedJobIds.has(job.job_id)}
                  onPublish={() => {
                    setPublishingJob(job);
                    setShowPublishModal(true);
                  }}
                  onExportToSharePoint={() => exportToSharePoint(job)}
                  onToggleActivity={() =>
                    setExpandedActivityJobId((prev) =>
                      prev === job.job_id ? null : job.job_id
                    )
                  }
                  isActivityOpen={expandedActivityJobId === job.job_id}
                  onToggleGapReport={() =>
                    setExpandedGapReportJobId((prev) =>
                      prev === job.job_id ? null : job.job_id
                    )
                  }
                  isGapReportOpen={expandedGapReportJobId === job.job_id}
                  scraperApiUrl={SCRAPER_API_URL}
                  getHeaders={getHeaders}
                  m365Connected={m365Connected}
                />
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Create Job Modal */}
      {showCreateModal && (
        <CreateScraperJobModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createJob}
        />
      )}

      {/* Results Modal */}
      {showResultsModal && selectedJob && jobResults && (
        <ScraperJobResultsModal
          job={selectedJob}
          results={jobResults}
          onClose={() => {
            setShowResultsModal(false);
            setSelectedJob(null);
            setJobResults(null);
          }}
        />
      )}

      {/* Publish to Index Modal */}
      {showPublishModal && publishingJob && (
        <PublishToIndexModal
          job={publishingJob}
          indexes={azureIndexes}
          onClose={() => {
            setShowPublishModal(false);
            setPublishingJob(null);
          }}
          onIndexCreated={(index) => addAzureIndex(index)}
          onPublish={async (indexId: number) => {
            const result = await publishJob(indexId, publishingJob.job_id);
            if (result.success) {
              setPublishedJobIds(prev => new Set(prev).add(publishingJob.job_id));
              toastAlert.displayToast(
                Severity.Success,
                `"${publishingJob.name}" published — taking you to Test Chat`
              );
              // Navigate to Test Chat for the bot config that owns this index
              const target = aiConfigurations.find(c => c.indexName && azureIndexes.find(ix => ix.id === indexId && ix.name === c.indexName));
              const configId = target?.id ?? aiConfigurations[0]?.id;
              if (configId) {
                navigate(`/ai-new/settings/test-chat/${configId}`);
              }
            } else {
              toastAlert.displayToast(
                Severity.Error,
                result.error || "Failed to publish job to index"
              );
            }
          }}
        />
      )}

      {/* SharePoint Export Modal */}
      {showSharePointExportModal && spExportJob && (
        <SharePointExportModal
          job={spExportJob}
          results={spExportResults}
          onClose={() => {
            setShowSharePointExportModal(false);
            setSpExportJob(null);
            setSpExportResults([]);
          }}
          onSuccess={(sharepointUrl, fileName) => {
            setShowSharePointExportModal(false);
            setSpExportJob(null);
            setSpExportResults([]);
            toastAlert.displayToast(
              Severity.Success,
              sharepointUrl
                ? `Exported to SharePoint: ${fileName}`
                : `Exported ${fileName} to SharePoint`
            );
          }}
        />
      )}
    </div>
  );
}
